/**
 * The starter finish — product on every wall and the ceiling of a new room in
 * one go, from the last step of the create wizard (planner/CreateRoomWizard).
 *
 *   - every wall: Wall&CeilingBoard in one colour and direction;
 *   - the walls picked for it: a SlatWall band across the whole wall, whole 6"
 *     rows, centred on the level part of the wall (the eave height — so the
 *     band lands at the same height and size on every wall, as a quick layout's
 *     does), boards below and above it; on a wall under a slope the top board
 *     runs on up into the peak;
 *   - the ceiling (flat, or every plane of a profile): one board fill, or none.
 *
 * The bands stack and touch, as a quick layout's do (sections/quickLayouts.ts);
 * the trim rules handle the joins.
 */

import { PanelOrientation, ProductColor } from '../../types';
import { RoomSurfaces, Section, Surface } from '../../types/sections';
import { SLATWALL_COVERAGE_HEIGHT } from '../constants';
import { MIN_SECTION_SIZE_FT } from '../sections/validation';
import { nextSectionId } from '../sections/edits';
import { FloorPlan, PlanResult, PlanRoom, applyRoomEdit, findRoom, roomSurfacesWithOutlines } from './floorPlan';

const EPS = 1e-6;
const ROW = SLATWALL_COVERAGE_HEIGHT;

export interface StarterFinish {
  board: { color: ProductColor; orientation: PanelOrientation };
  /** The band: its colour and its share of the wall height (percent). */
  slatwall: { color: ProductColor; percent: number };
  /** Which walls get the band, in `PLAN_FACES` order (front, right, back, left). */
  slatwallWalls: boolean[];
  /** The ceiling's board colour; null for a bare ceiling. */
  ceiling: ProductColor | null;
}

export interface SlatwallBand {
  /** Bottom of the band, from the floor (feet, whole inches). */
  y: number;
  height: number;
  rows: number;
}

/**
 * The band `percent` of a wall `level` feet tall makes: the nearest whole rows
 * (at least one), centred — unless that leaves a strip of board too narrow to
 * lay, when it drops to the floor or loses a row. Null when not even one row fits.
 */
export function slatwallBand(level: number, percent: number): SlatwallBand | null {
  const most = Math.floor(level / ROW + EPS);
  if (most < 1) return null;
  const wanted = Math.min(most, Math.max(1, Math.round((level * Math.max(0, Math.min(100, percent))) / 100 / ROW)));
  for (let rows = wanted; rows >= 1; rows--) {
    const height = rows * ROW;
    let y = Math.round(((level - height) / 2) * 12) / 12;
    if (y < MIN_SECTION_SIZE_FT - EPS) y = 0;
    const above = level - y - height;
    if (above < -EPS) continue;
    if (above > EPS && above < MIN_SECTION_SIZE_FT - EPS) {
      // Too thin a strip on top: put it all below instead, if that is wide enough.
      const below = level - height;
      if (below >= MIN_SECTION_SIZE_FT - EPS) return { y: below, height, rows };
      continue;
    }
    return { y, height, rows };
  }
  return null;
}

/** The sections of one wall: boards, with the band across it when `band` is given. */
export function starterWallSections(surface: Surface, band: SlatwallBand | null, finish: StarterFinish): Section[] {
  const { color, orientation } = finish.board;
  const sections: Section[] = [];
  let growing: Surface = { ...surface, sections: [] };
  const push = (s: Omit<Section, 'id'>) => {
    if (s.height < MIN_SECTION_SIZE_FT - EPS || s.width < MIN_SECTION_SIZE_FT - EPS) return;
    const section = { ...s, id: nextSectionId(growing, s.productType) } as Section;
    sections.push(section);
    growing = { ...growing, sections: [...growing.sections, section] };
  };
  const board = (y: number, height: number) => push({ productType: 'WCB', x: 0, y, width: surface.width, height, color, orientation });
  if (!band || band.y + band.height > surface.height + EPS) {
    board(0, surface.height);
    return sections;
  }
  if (band.y > EPS) board(0, band.y);
  push({ productType: 'SlatWall', x: 0, y: band.y, width: surface.width, height: band.height, color: finish.slatwall.color });
  const top = band.y + band.height;
  board(top, surface.height - top);
  return sections;
}

/** A ceiling surface's sections: one board fill, or none. */
function ceilingSections(surface: Surface, color: ProductColor | null): Section[] {
  if (!color) return [];
  return [{ id: nextSectionId({ ...surface, sections: [] }, 'WCB'), productType: 'WCB', x: 0, y: 0, width: surface.width, height: surface.height, color, orientation: 'Horizontal' }];
}

/** The room with its walls and ceiling finished; what was on them is replaced (openings stay). */
export function applyStarterFinish(plan: FloorPlan, roomId: string, finish: StarterFinish): PlanResult {
  const room = findRoom(plan, roomId);
  if (!room) return { ok: false, reason: 'not-found' };
  const read = roomSurfacesWithOutlines(room);
  const band = slatwallBand(room.height, finish.slatwall.percent);
  const surfaces: RoomSurfaces = {
    ...read,
    walls: read.walls.map((wall, i) => ({
      ...wall,
      sections: starterWallSections(wall, finish.slatwallWalls[i] ? band : null, finish),
    })),
  };
  if (read.ceiling) surfaces.ceiling = { ...read.ceiling, sections: ceilingSections(read.ceiling, finish.ceiling) };
  if (read.ceilingPlanes) {
    surfaces.ceilingPlanes = Object.fromEntries(
      Object.entries(read.ceilingPlanes).map(([id, plane]) => [id, { ...plane, sections: ceilingSections(plane, finish.ceiling) }]),
    );
  }
  return applyRoomEdit(plan, roomId, surfaces);
}

/**
 * The starter finish a room's product reads as, to start editing from: the
 * first board's colour and direction, the first band's colour and share of the
 * wall, which walls carry SlatWall, the ceiling's colour. `fallback` fills
 * what the room does not say — and is all of it for a bare room.
 */
export function finishOfRoom(room: PlanRoom, fallback: StarterFinish): StarterFinish {
  const walls = room.surfaces.walls;
  const ceilings = [...(room.surfaces.ceiling ? [room.surfaces.ceiling] : []), ...Object.values(room.surfaces.ceilingPlanes ?? {})];
  const all = [...walls, ...ceilings].flatMap((s) => s.sections);
  if (all.length === 0) return fallback;
  const board = walls.flatMap((w) => w.sections).find((s) => s.productType === 'WCB');
  const slat = walls.flatMap((w) => w.sections).find((s) => s.productType === 'SlatWall');
  const ceiling = ceilings.flatMap((c) => c.sections).find((s) => s.productType === 'WCB');
  return {
    board: board ? { color: board.color, orientation: board.orientation ?? fallback.board.orientation } : fallback.board,
    slatwall: slat
      ? { color: slat.color, percent: Math.max(5, Math.min(100, Math.round((slat.height / room.height) * 100))) }
      : fallback.slatwall,
    slatwallWalls: walls.map((w) => w.sections.some((s) => s.productType === 'SlatWall')),
    ceiling: ceiling ? ceiling.color : null,
  };
}

/** Which walls a pattern picks, in `PLAN_FACES` order. */
export type SlatwallPattern = 'all' | 'front-back' | 'sides' | 'back' | 'none';
export const SLATWALL_PATTERNS: Record<SlatwallPattern, boolean[]> = {
  all: [true, true, true, true],
  'front-back': [true, false, true, false],
  sides: [false, true, false, true],
  back: [false, false, true, false],
  none: [false, false, false, false],
};
/** The pattern these walls are, if they are one. */
export function slatwallPatternOf(walls: boolean[]): SlatwallPattern | null {
  const hit = (Object.keys(SLATWALL_PATTERNS) as SlatwallPattern[]).find((p) => SLATWALL_PATTERNS[p].every((v, i) => v === !!walls[i]));
  return hit ?? null;
}
